import { useMemo, useState } from 'react';

import { CLAIM_FIELDS, ClaimFieldSpec } from './cargo-docs.data';
import '../ListZeroWorkflow/list-zero-workflow.scss';

/*
 * What travels with each call, and in what format.
 *
 * Grouped by the call rather than listed alphabetically: the question this
 * answers is "what do I send with this request", and a flat A to Z list buries
 * that. The filter searches the field and the call together, so typing
 * "credit" narrows to the credit note.
 */

const GROUPS = [
  'Notification',
  'Details',
  'Document upload',
  'Decision',
  'Credit note',
];

function FieldRow({ spec }: { spec: ClaimFieldSpec }) {
  return (
    <tr>
      <td>
        <div className="fields-name">{spec.field}</div>
        {spec.required ? (
          <span className="fields-tag">Required</span>
        ) : (
          <div className="fields-label">Optional</div>
        )}
      </td>
      <td>
        <div>{spec.format}</div>
        {spec.note && <div className="fields-note">{spec.note}</div>}
      </td>
    </tr>
  );
}

export default function CargoClaimsFields() {
  const [query, setQuery] = useState('');

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();

    if (!q) return CLAIM_FIELDS;

    return CLAIM_FIELDS.filter(
      (spec) =>
        spec.field.toLowerCase().includes(q) ||
        spec.sentWith.toLowerCase().includes(q) ||
        spec.format.toLowerCase().includes(q)
    );
  }, [query]);

  const groups = GROUPS.map((group) => ({
    group,
    rows: matches.filter((spec) => spec.sentWith === group),
  })).filter((entry) => entry.rows.length > 0);

  return (
    <div className="list-zero-workflow">
      <section className="workflow-card">
        <h2>Claim Fields</h2>
        <p>
          Every field that travels with a cargo claim, grouped by the call that
          carries it. Required fields are marked; everything else can be left out
          and filled in later through the details call.
        </p>

        <div className="workflow-callout">
          Amounts are sent as text rather than as numbers, for example
          <code> "1250.00"</code> rather than <code> 1250.00</code>. A large total
          sent as a number can lose its last digits in transit, and a claim total
          that is nearly right is worse than one that fails outright.
        </div>

        <input
          className="fields-search"
          type="text"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Filter by field or call, e.g. shipmentNo or credit note"
        />
      </section>

      {groups.length === 0 ? (
        <section className="workflow-card">
          <p className="status-empty">No field matches "{query}".</p>
        </section>
      ) : (
        groups.map(({ group, rows }) => (
          <section className="workflow-card" key={group}>
            <h2>{group}</h2>
            <table className="workflow-table fields-table">
              <thead>
                <tr>
                  <th style={{ width: 240 }}>Field</th>
                  <th>Format</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((spec) => (
                  <FieldRow key={`${spec.sentWith}-${spec.field}`} spec={spec} />
                ))}
              </tbody>
            </table>
          </section>
        ))
      )}
    </div>
  );
}
